/* ============================================================
   seedQueries.js — seed data served through the query cache.

   Two jobs:

   1. Make swapping a screen to a real endpoint a ONE-LINE change. A screen does
        const { data: jobs = [] } = useQuery(seedQuery('jobs'))
      and the day GET /jobs/fetch exists that becomes
        const { data: jobs = [] } = useQuery({ queryKey: qk.jobs.list(f),
                                               queryFn: () => jobsApi.list(f) })
      with the component body, the JSX and the table config untouched.

   2. Give the seed a mutation model. The prototype mutated the DB global in
      place and called Router.reload(); here the CACHE is the store, and every
      mutation is a setQueryData that re-renders exactly the subscribed screens.
   ============================================================ */

import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { qk } from '../lib/queryKeys'
import * as seed from './seed'

/**
 * gcTime: Infinity is load-bearing, not a micro-optimisation. These buckets are
 * mutable app state, not server state — if a mutated bucket were garbage
 * collected after sitting off-screen it would silently revert to the pristine
 * seed on the next mount, which looks like data loss and is miserable to debug.
 */
export function seedQuery(bucket) {
  return {
    queryKey: qk.seed[bucket](),
    queryFn: async () => seed[bucket],
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  }
}

/** Buckets pre-populated at boot because the SHELL reads them before any route mounts. */
export const SHELL_BUCKETS = ['jobs', 'notifications', 'messages', 'tasks', 'inbox']

/** Everything the cache owns. The reference lists are imported directly instead. */
export const SEED_BUCKETS = [
  ...SHELL_BUCKETS,
  'candidates', 'interviews', 'assessments', 'offers', 'activity', 'emails',
  'publishings', 'rbacRoles', 'recruiters', 'managers', 'users',
]

/**
 * Seed the shell's buckets before first render so the sidebar badges and the
 * topbar dropdowns paint with real counts instead of popping in a frame later.
 * This is the "cache initialized" step, called once from main.jsx.
 */
export function initializeCache(queryClient) {
  for (const bucket of SHELL_BUCKETS) {
    queryClient.setQueryData(qk.seed[bucket](), seed[bucket])
  }
  // Runtime buckets the prototype kept on DB. `favorites` and `recentlyViewed`
  // restore from localStorage — a "recently viewed" list that empties on every
  // refresh is worse than not having one.
  queryClient.setQueryData(qk.seed.favorites(), readPersisted('tf-favorites', seed.favorites))
  queryClient.setQueryData(qk.seed.recentlyViewed(), readPersisted('tf-recent', []))
}

function readPersisted(key, fallback) {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}

export function persist(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* ignore */
  }
}

/**
 * Mutate a seed bucket. Returns `update(fn)` where fn maps the old value to the
 * new one — the same shape as a React setState updater.
 *
 *   const updateCandidates = useSeedMutation('candidates')
 *   updateCandidates(cs => cs.map(c => c.id === id ? { ...c, stage } : c))
 */
export function useSeedMutation(bucket) {
  const qc = useQueryClient()
  return useCallback(
    (updater) => {
      const key = qk.seed[bucket]()
      qc.setQueryData(key, (old) => updater(old ?? seed[bucket]))
      return qc.getQueryData(key)
    },
    [qc, bucket],
  )
}
